import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useApi from "@/hooks/useApi";
import {
  PrimaryButton,
  SecondaryButton,
  GhostButton,
  Modal,
  Divider,
  ConfirmDialog,
  Check,
  ArrowLeft,
  ArrowRight,
  FieldInput,
  IconButton
} from "@/components/ds";

/**
 * CallRailSetupWizard — a thin wizard modeled on IvrSetupWizard.tsx's StepBar
 * pattern (client/src/pages/Messaging/Channels/IvrSetupWizard.tsx).
 * It does not itself persist config — it is a guided router to the two real
 * setup surfaces (CallRail settings page / IVR number settings page).
 *
 * Re-entrant per CLAUDE.md "WIZARD DESIGN STANDARD — ALL INTEGRATIONS AND
 * WALKTHROUGHS": on open, detects existing setup (a configured CallRail
 * account OR any campaign-labeled IVR numbers) and, if found, presents an
 * explicit Add/extend vs Edit existing vs Start over choice instead of
 * silently re-running step 1. "Start over" is gated behind ConfirmDialog and
 * never wipes anything itself — the wizard has no destructive backend calls
 * of its own; it only routes to the real edit surfaces, which already own
 * their own upsert semantics.
 */

type Step = "loading" | "manage" | "choose" | "setup" | "edit" | "done";
type Track = "callrail" | "native" | null;

const FLOW_STEP_LABELS: Record<
  Exclude<Step, "loading" | "manage" | "edit">,
  string
> = {
  choose: "How you track calls",
  setup: "Setup",
  done: "Done"
};
const FLOW_STEPS: Exclude<Step, "loading" | "manage" | "edit">[] = [
  "choose",
  "setup",
  "done"
];

interface LabeledNumber {
  id: number;
  phoneNumber: string;
  label?: string;
  campaignLabel?: string;
}

interface CallRailConfigSummary {
  configured: boolean;
  accountId?: string;
  isEnabled?: boolean;
  webhookUrl?: string;
  webhookSecretMasked?: string;
}

function StepBar({ current }: { current: Step }) {
  if (current === "loading" || current === "manage" || current === "edit")
    return null;
  const idx = FLOW_STEPS.indexOf(current as (typeof FLOW_STEPS)[number]);
  return (
    <div className="flex items-center gap-0 mb-8">
      {FLOW_STEPS.map((s, i) => {
        const done = i < idx;
        const active = s === current;
        return (
          <React.Fragment key={s}>
            <div className="flex flex-col items-center gap-1">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-colors ${
                  done
                    ? "bg-(--ds-primary) border-(--ds-primary) text-white"
                    : active
                      ? "border-(--ds-primary) text-(--ds-primary) bg-white"
                      : "border-zinc-300 text-zinc-400 bg-white"
                }`}
              >
                {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
              </div>
              <span
                className={`text-xs font-medium ${active ? "text-(--ds-primary)" : done ? "text-zinc-600" : "text-zinc-400"}`}
              >
                {FLOW_STEP_LABELS[s]}
              </span>
            </div>
            {i < FLOW_STEPS.length - 1 && (
              <div
                className={`flex-1 h-0.5 mb-4 mx-1 ${i < idx ? "bg-(--ds-primary)" : "bg-zinc-200"}`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

interface Props {
  webhookUrl: string | null;
  onClose(): void;
}

export default function CallRailSetupWizard({ webhookUrl, onClose }: Props) {
  const api = useApi();

  const [step, setStep] = useState<Step>("loading");
  const [track, setTrack] = useState<Track>(null);
  const [cameFromStartOver, setCameFromStartOver] = useState(false);
  const [showStartOverConfirm, setShowStartOverConfirm] = useState(false);

  // ── Existing-setup detection ─────────────────────────────────────────────
  const [config, setConfig] = useState<CallRailConfigSummary | null>(null);
  const [labeledNumbers, setLabeledNumbers] = useState<LabeledNumber[]>([]);
  const [detectError, setDetectError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function detect() {
      let cfg: CallRailConfigSummary | null = null;
      let numbers: LabeledNumber[] = [];
      try {
        const resp = await api.admin.get("CallRail/config");
        if (resp.ok) cfg = await resp.json();
      } catch (err) {
        if (!cancelled)
          setDetectError(
            `Failed to check existing setup: ${err instanceof Error ? err.message : String(err)}`
          );
      }
      try {
        const resp = await api.messaging.get("Sms/Integrations");
        if (resp.ok) {
          const data = await resp.json();
          numbers = Array.isArray(data)
            ? data.filter(
                (i: LabeledNumber) =>
                  !!(i.campaignLabel && i.campaignLabel.trim())
              )
            : [];
        }
      } catch {
        // non-fatal — campaign-label detection is best-effort, the wizard still works without it
      }
      if (cancelled) return;
      setConfig(cfg);
      setLabeledNumbers(numbers);
      const hasExistingSetup = !!cfg?.configured || numbers.length > 0;
      setStep(hasExistingSetup ? "manage" : "choose");
    }
    detect();
    return () => {
      cancelled = true;
    };
  }, [api.admin, api.messaging]);

  const hasExistingSetup = !!config?.configured || labeledNumbers.length > 0;

  // ── Navigation ────────────────────────────────────────────────────────────
  function nextStep() {
    if (step === "choose" && track) setStep("setup");
    else if (step === "setup") setStep("done");
  }

  function prevStep() {
    if (step === "setup") setStep("choose");
    else if (step === "choose" && hasExistingSetup) setStep("manage");
    else if (step === "edit") setStep("manage");
    else if (step === "done") setStep("setup");
  }

  function startAddOrExtend() {
    setCameFromStartOver(false);
    setStep("choose");
  }

  function startEdit() {
    setStep("edit");
  }

  function confirmStartOver() {
    setShowStartOverConfirm(false);
    setCameFromStartOver(true);
    setTrack(null);
    setStep("choose");
  }

  const isFirst = step === "choose" && !hasExistingSetup;
  const isDone = step === "done";
  const isManage = step === "manage";
  const isEdit = step === "edit";

  return (
    <>
      <Modal
        open
        size="lg"
        height="tall"
        shellClassName="mx-4"
        onClose={onClose}
        closeOnEsc={false}
        closeOnBackdropClick={false}
      >
        <div className="px-8 pt-8 pb-0">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-lg font-semibold text-zinc-800">
              Set Up Call Attribution
            </h1>
            <IconButton
              onClick={onClose}
              aria-label="Close setup wizard"
              className="!text-xl leading-none"
            >
              &times;
            </IconButton>
          </div>
          <StepBar current={step} />
        </div>

        <div className="flex-1 overflow-y-auto px-8 pb-2">
          {step === "loading" && (
            <div
              className="py-16 text-center text-sm text-zinc-400"
              aria-live="polite"
            >
              Checking your existing setup...
            </div>
          )}

          {detectError && step !== "loading" && (
            <div
              className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-700"
              role="alert"
            >
              {detectError} You can still continue below.
            </div>
          )}

          {isManage && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-zinc-800 mb-1">
                  You already have call attribution set up
                </h2>
                <p className="text-sm text-zinc-500">
                  {config?.configured &&
                    `CallRail is connected${config.accountId ? ` (account ${config.accountId})` : ""}. `}
                  {labeledNumbers.length > 0 &&
                    `${labeledNumbers.length} IVR number${labeledNumbers.length !== 1 ? "s" : ""} tagged with a campaign label. `}
                  What would you like to do?
                </p>
              </div>

              <button
                type="button"
                onClick={startAddOrExtend}
                className="w-full text-left border-2 border-zinc-200 hover:border-(--ds-primary) rounded-xl p-4 transition-colors"
              >
                <span className="font-medium text-sm text-zinc-800">
                  Add / extend
                </span>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Tag another number with a campaign, or add another CallRail
                  data source. Your existing setup is untouched.
                </p>
              </button>

              <button
                type="button"
                onClick={startEdit}
                className="w-full text-left border-2 border-zinc-200 hover:border-(--ds-primary) rounded-xl p-4 transition-colors"
              >
                <span className="font-medium text-sm text-zinc-800">
                  Edit existing
                </span>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Review your current CallRail connection and labeled numbers,
                  with links to change them.
                </p>
              </button>

              <button
                type="button"
                onClick={() => setShowStartOverConfirm(true)}
                className="w-full text-left border-2 border-red-200 hover:border-red-400 rounded-xl p-4 transition-colors"
              >
                <span className="font-medium text-sm text-red-700">
                  Start over
                </span>
                <p className="text-xs text-red-500 mt-0.5">
                  Walk through setup from scratch. Requires confirmation —
                  nothing is changed until you confirm on the next screen.
                </p>
              </button>
            </div>
          )}

          {isEdit && (
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-semibold text-zinc-800 mb-1">
                  Your current setup
                </h2>
                <p className="text-sm text-zinc-500">
                  Jump directly into either surface to make a change — this
                  wizard doesn't own that data, so it always shows the current
                  live values.
                </p>
              </div>

              <div className="rounded-xl border border-zinc-200 p-4">
                <div className="text-sm font-semibold text-zinc-800 mb-2">
                  CallRail connection
                </div>
                {config?.configured ? (
                  <div className="text-sm text-zinc-600 space-y-1">
                    <div>
                      Account:{" "}
                      <span className="font-mono">
                        {config.accountId || "(not set)"}
                      </span>
                    </div>
                    <div>
                      Webhook secret:{" "}
                      <span className="font-mono">
                        {config.webhookSecretMasked ?? "Not generated yet"}
                      </span>
                    </div>
                    <div>
                      Status: {config.isEnabled ? "Enabled" : "Disabled"}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-zinc-500">Not connected yet.</p>
                )}
                <Link
                  to="/integrations/callrail"
                  className="inline-block mt-3 text-sm text-(--ds-primary) hover:underline"
                >
                  Edit CallRail settings &rarr;
                </Link>
              </div>

              <div className="rounded-xl border border-zinc-200 p-4">
                <div className="text-sm font-semibold text-zinc-800 mb-2">
                  Campaign-labeled numbers
                </div>
                {labeledNumbers.length === 0 ? (
                  <p className="text-sm text-zinc-500">
                    No numbers tagged yet.
                  </p>
                ) : (
                  <ul className="text-sm text-zinc-600 space-y-1">
                    {labeledNumbers.map((n) => (
                      <li
                        key={n.id}
                        className="flex items-center justify-between gap-3"
                      >
                        <span className="font-mono">{n.phoneNumber}</span>
                        <span className="text-zinc-500 truncate">
                          {n.campaignLabel}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                <Link
                  to="/ivr"
                  className="inline-block mt-3 text-sm text-(--ds-primary) hover:underline"
                >
                  Edit IVR number labels &rarr;
                </Link>
              </div>
            </div>
          )}

          {step === "choose" && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-zinc-800 mb-1">
                  How do you track calls today?
                </h2>
                <p className="text-sm text-zinc-500">
                  This decides which setup path we walk you through next.
                </p>
              </div>

              {cameFromStartOver && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700">
                  Starting over won't change anything until you take an action
                  on the next screen (e.g. clicking Regenerate). Your existing
                  CallRail connection and campaign labels stay as-is unless you
                  explicitly change them there.
                </div>
              )}

              <button
                type="button"
                onClick={() => setTrack("callrail")}
                className={`w-full text-left border-2 rounded-xl p-4 transition-colors ${track === "callrail" ? "border-(--ds-primary) bg-blue-50" : "border-zinc-200 bg-white"}`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${track === "callrail" ? "border-(--ds-primary)" : "border-zinc-300"}`}
                  >
                    {track === "callrail" && (
                      <div className="w-2 h-2 rounded-full bg-(--ds-primary)" />
                    )}
                  </div>
                  <div>
                    <span className="font-medium text-sm text-zinc-800">
                      I use CallRail
                    </span>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      We'll walk you through connecting your CallRail webhook.
                    </p>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setTrack("native")}
                className={`w-full text-left border-2 rounded-xl p-4 transition-colors ${track === "native" ? "border-(--ds-primary) bg-blue-50" : "border-zinc-200 bg-white"}`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${track === "native" ? "border-(--ds-primary)" : "border-zinc-300"}`}
                  >
                    {track === "native" && (
                      <div className="w-2 h-2 rounded-full bg-(--ds-primary)" />
                    )}
                  </div>
                  <div>
                    <span className="font-medium text-sm text-zinc-800">
                      I don't use a third-party tool
                    </span>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      We'll show you how to tag your existing IVR numbers with a
                      campaign label instead.
                    </p>
                  </div>
                </div>
              </button>
            </div>
          )}

          {step === "setup" && track === "callrail" && (
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-semibold text-zinc-800 mb-1">
                  Connect CallRail
                </h2>
                <p className="text-sm text-zinc-500">
                  Three quick steps in your CallRail account.
                </p>
              </div>
              <ol className="list-decimal list-inside space-y-2 text-sm text-zinc-600">
                <li>
                  Go to{" "}
                  <Link
                    to="/integrations/callrail"
                    className="text-(--ds-primary) hover:underline"
                  >
                    CallRail Attribution settings
                  </Link>{" "}
                  and click <strong>Regenerate</strong> to create your webhook
                  secret, if you haven't already.
                </li>
                <li>
                  Copy the Webhook URL
                  {webhookUrl ? "" : " shown on the settings page"}.
                </li>
                <li>
                  In your CallRail account, go to{" "}
                  <strong>Settings &gt; Webhooks &gt; Add Webhook</strong>,
                  paste the URL, and save.
                </li>
              </ol>
              {webhookUrl && (
                <FieldInput
                  className="w-full font-mono text-xs"
                  readOnly
                  aria-label="CallRail webhook URL"
                  value={webhookUrl}
                />
              )}
            </div>
          )}

          {step === "setup" && track === "native" && (
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-semibold text-zinc-800 mb-1">
                  Tag your IVR numbers
                </h2>
                <p className="text-sm text-zinc-500">
                  No CallRail account needed — Velaro tracks the calls for you.
                </p>
              </div>
              <ol className="list-decimal list-inside space-y-2 text-sm text-zinc-600">
                <li>
                  Go to your{" "}
                  <Link
                    to="/ivr"
                    className="text-(--ds-primary) hover:underline"
                  >
                    IVR number settings
                  </Link>
                  .
                </li>
                <li>
                  Click Edit on any number and add a Campaign Label, e.g. "Radio
                  Ad - Spring 2026".
                </li>
                <li>
                  Every call to that number automatically shows up on the Call
                  Attribution report tagged with that campaign. No other setup
                  required.
                </li>
              </ol>
            </div>
          )}

          {step === "done" && (
            <div className="text-center space-y-6 py-4">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-zinc-800 mb-2">
                  You're set up
                </h2>
                <p className="text-sm text-zinc-500 max-w-md mx-auto">
                  {track === "callrail"
                    ? "Once CallRail sends its first webhook event, calls will start appearing on your report."
                    : "Once a call comes in to a labeled number, it will start appearing on your report."}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center mt-2">
                <Link to="/reports/call-attribution">
                  <PrimaryButton onClick={onClose}>
                    Go to Call Attribution report
                  </PrimaryButton>
                </Link>
                <SecondaryButton onClick={onClose}>Close</SecondaryButton>
              </div>
            </div>
          )}
        </div>

        {!isDone && step !== "loading" && (
          <>
            <Divider className="my-0" />
            <div className="px-8 py-6 flex items-center justify-between">
              <div>
                {isManage ? (
                  <GhostButton onClick={onClose}>Cancel</GhostButton>
                ) : !isFirst ? (
                  <GhostButton onClick={prevStep}>
                    <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back
                  </GhostButton>
                ) : (
                  <GhostButton onClick={onClose}>Cancel</GhostButton>
                )}
              </div>
              {isEdit ? (
                <PrimaryButton onClick={onClose}>Done</PrimaryButton>
              ) : !isManage ? (
                <PrimaryButton
                  onClick={nextStep}
                  disabled={step === "choose" && !track}
                >
                  {step === "setup" ? (
                    "Finish"
                  ) : (
                    <span className="flex items-center gap-1">
                      Next <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  )}
                </PrimaryButton>
              ) : null}
            </div>
          </>
        )}
      </Modal>

      <ConfirmDialog
        open={showStartOverConfirm}
        title="Start over?"
        message="This walks you through setup from scratch. It will NOT automatically delete your existing CallRail connection or campaign labels — those only change if you explicitly edit or regenerate them on their own settings pages. Continue?"
        confirmLabel="Start over"
        variant="danger"
        onConfirm={confirmStartOver}
        onCancel={() => setShowStartOverConfirm(false)}
      />
    </>
  );
}
